import type { FormEvent } from "react";
import { Button } from "../../components/Button";
import { ChoiceGroup, type Choice } from "../../components/ChoiceGroup";
import { ErrorSummary } from "../../components/ErrorSummary";
import { ExplainerDisclosure } from "../../components/ExplainerDisclosure";
import { LoadingState } from "../../components/LoadingState";
import { MultiChoiceGroup, type MultiChoice } from "../../components/MultiChoiceGroup";
import { Progress } from "../../components/Progress";
import { useContent } from "../../content/ContentContext";
import type { Answers, FastProfileQuestion } from "../../types";

type Step = 1 | 2 | 3 | 4 | 5;

interface Props {
  step: Step;
  answers: Answers;
  errors: Record<string, string>;
  busy: boolean;
  online: boolean;
  onChange: <K extends keyof Answers>(key: K, value: Answers[K]) => void;
  onSubmit: (step: Step) => void;
  onBack: () => void;
  onAnnounce?: (message: string) => void;
}

/**
 * Every legend, hint and option label below comes from
 * `GET /api/v1/content` via `ContentProvider`, resolved by backend field
 * name. There is no local label array here any more — the twelve module
 * constants this file used to hold (`MUNICIPALITIES`, `YES_NO`,
 * `YES_NO_UNSURE`, `REGISTRATION`, `RESIDENCE`, `ACTIVITIES`, `BUSINESS`,
 * `HOUSING`, `HOUSEHOLDS`, `INSURANCE`, `ASSETS`, `VEHICLES`) were the
 * frontend code-to-copy map W4-01 removes; see
 * `docs/architecture/features/W4-01.md`.
 *
 * Which fields exist, their order, and their conditional visibility are
 * unchanged and still governed entirely by this component's own JSX below
 * and by `planner.journey.services.CONDITIONALS` on the server — the bundle
 * supplies wording only, never structure.
 */
function optionsToChoices(question: FastProfileQuestion | undefined): Choice[] {
  return (question?.options ?? []).map((option) => ({
    value: option.value,
    label: option.label,
    description: option.description,
    explainerKey: option.explainer_keys?.[0],
  }));
}

function optionsToMultiChoices(question: FastProfileQuestion | undefined): MultiChoice[] {
  return (question?.options ?? []).map((option) => ({
    value: option.value,
    label: option.label,
    description: option.description,
    explainerKey: option.explainer_keys?.[0],
  }));
}

export function QuestionnairePage({
  step,
  answers,
  errors,
  busy,
  online,
  onChange,
  onSubmit,
  onBack,
  onAnnounce,
}: Props) {
  const { status, questionsByField, retry } = useContent();

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit(step);
  };
  const setMulti = (
    key: "activities" | "assetIncomeFlags",
    values: string[],
    message: string,
  ) => {
    onChange(key, values as Answers[typeof key]);
    if (message) onAnnounce?.(message);
  };

  if (status === "loading") {
    return (
      <div className="page page--narrow">
        <LoadingState label="Preparing your questions" />
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="page page--narrow">
        <p className="eyebrow">Fast profile</p>
        <h1>Tell us a little about your situation</h1>
        <ErrorSummary
          errors={{ page: "We couldn’t load the questions. Check your connection and try again." }}
        />
        <Button onClick={retry}>Try again</Button>
      </div>
    );
  }

  const q = (field: string) => questionsByField[field];

  return (
    <div className="page page--narrow">
      <p className="eyebrow">Fast profile</p>
      <h1>Tell us a little about your situation</h1>
      <p>These answers help us find the Dutch costs, benefits and actions that may apply to you.</p>
      <p className="field-hint">We’ll ask for more detail later only if it can improve an estimate.</p>
      <Progress current={step} total={5} />
      <ErrorSummary errors={errors} />
      <form className="question-card" onSubmit={submit} noValidate>
        {step === 1 && (
          <>
            <ChoiceGroup
              id="municipalityContext"
              legend={q("municipality_context").legend}
              choices={optionsToChoices(q("municipality_context"))}
              value={answers.municipalityContext}
              error={errors.municipalityContext}
              explainerKey={q("municipality_context").explainer_keys?.[0]}
              onChange={(value) => onChange("municipalityContext", value)}
            />
            <ChoiceGroup
              id="alreadyInNetherlands"
              legend={q("already_in_netherlands").legend}
              choices={optionsToChoices(q("already_in_netherlands"))}
              value={answers.alreadyInNetherlands}
              error={errors.alreadyInNetherlands}
              onChange={(value) => onChange("alreadyInNetherlands", value)}
            />
            <div className={`field ${errors.arrivalDate ? "field--error" : ""}`}>
              <label htmlFor="arrivalDate">{q("arrival_date").legend}</label>
              <p id="arrivalDate-hint" className="field-hint">
                {q("arrival_date").hint}
              </p>
              {errors.arrivalDate && (
                <p id="arrivalDate-error" className="field-error">{errors.arrivalDate}</p>
              )}
              <input
                id="arrivalDate"
                type="date"
                value={answers.arrivalDate}
                aria-invalid={Boolean(errors.arrivalDate)}
                aria-describedby={`arrivalDate-hint${errors.arrivalDate ? " arrivalDate-error" : ""}`}
                onChange={(event) => onChange("arrivalDate", event.target.value)}
              />
            </div>
            <ChoiceGroup
              id="stayOverFourMonths"
              legend={q("stay_over_four_months").legend}
              hint={q("stay_over_four_months").hint}
              choices={optionsToChoices(q("stay_over_four_months"))}
              value={answers.stayOverFourMonths}
              error={errors.stayOverFourMonths}
              onChange={(value) => onChange("stayOverFourMonths", value)}
            />
            {answers.alreadyInNetherlands === "yes" && (
              <ChoiceGroup
                id="registrationStatus"
                legend={q("registration_status").legend}
                hint={q("registration_status").hint}
                choices={optionsToChoices(q("registration_status"))}
                value={answers.registrationStatus}
                error={errors.registrationStatus}
                explainerKey={q("registration_status").explainer_keys?.[0]}
                onChange={(value) => onChange("registrationStatus", value)}
              />
            )}
          </>
        )}
        {step === 2 && (
          <>
            <ChoiceGroup
              id="residenceCategory"
              legend={q("residence_category").legend}
              choices={optionsToChoices(q("residence_category"))}
              value={answers.residenceCategory}
              error={errors.residenceCategory}
              explainerKey={q("residence_category").explainer_keys?.[0]}
              onChange={(value) => onChange("residenceCategory", value)}
            />
            <div className="field">
              <label htmlFor="permitExpiryDate">{q("permit_expiry_date").legend}</label>
              <p id="permitExpiryDate-hint" className="field-hint">{q("permit_expiry_date").hint}</p>
              <ExplainerDisclosure
                explainerKey={q("permit_expiry_date").explainer_keys?.[0]}
                className="explainer--stem"
              />
              {!answers.permitExpiryUnknown && (
                <input
                  id="permitExpiryDate"
                  type="date"
                  value={answers.permitExpiryDate}
                  aria-describedby="permitExpiryDate-hint"
                  onChange={(event) => onChange("permitExpiryDate", event.target.value)}
                />
              )}
              <label className="check-choice">
                <input
                  type="checkbox"
                  checked={answers.permitExpiryUnknown}
                  onChange={(event) => {
                    onChange("permitExpiryUnknown", event.target.checked);
                    if (event.target.checked) onChange("permitExpiryDate", "");
                  }}
                />
                I don’t know the expiry date
              </label>
            </div>
          </>
        )}
        {step === 3 && (
          <>
            <MultiChoiceGroup
              id="activities"
              legend={q("activities").legend}
              choices={optionsToMultiChoices(q("activities"))}
              values={answers.activities}
              error={errors.activities}
              explainerKey={q("activities").explainer_keys?.[0]}
              onChange={(values, message) => setMulti("activities", values, message)}
            />
            {(answers.activities.includes("employee")
              || answers.activities.includes("paid_intern")) && (
              <ChoiceGroup
                id="recruitedBeforeArrival"
                legend={q("recruited_before_arrival").legend}
                hint={q("recruited_before_arrival").hint}
                choices={optionsToChoices(q("recruited_before_arrival"))}
                value={answers.recruitedBeforeArrival}
                error={errors.recruitedBeforeArrival}
                onChange={(value) => onChange("recruitedBeforeArrival", value)}
              />
            )}
            {answers.activities.includes("freelancer_business_owner") && (
              <ChoiceGroup
                id="businessStatus"
                legend={q("business_status").legend}
                hint={q("business_status").hint}
                choices={optionsToChoices(q("business_status"))}
                value={answers.businessStatus}
                error={errors.businessStatus}
                explainerKey={q("business_status").explainer_keys?.[0]}
                onChange={(value) => onChange("businessStatus", value)}
              />
            )}
          </>
        )}
        {step === 4 && (
          <>
            <ChoiceGroup
              id="housingType"
              legend={q("housing_type").legend}
              hint={q("housing_type").hint}
              choices={optionsToChoices(q("housing_type"))}
              value={answers.housingType}
              error={errors.housingType}
              explainerKey={q("housing_type").explainer_keys?.[0]}
              onChange={(value) => onChange("housingType", value)}
            />
            {answers.housingType === "student_accommodation" && (
              <ChoiceGroup
                id="studentAccommodationFacilities"
                legend={q("student_accommodation_facilities").legend}
                hint={q("student_accommodation_facilities").hint}
                choices={optionsToChoices(q("student_accommodation_facilities"))}
                value={answers.studentAccommodationFacilities}
                error={errors.studentAccommodationFacilities}
                explainerKey={q("student_accommodation_facilities").explainer_keys?.[0]}
                onChange={(value) => onChange("studentAccommodationFacilities", value)}
              />
            )}
            <ChoiceGroup
              id="householdType"
              legend={q("household_type").legend}
              hint={q("household_type").hint}
              choices={optionsToChoices(q("household_type"))}
              value={answers.householdType}
              error={errors.householdType}
              explainerKey={q("household_type").explainer_keys?.[0]}
              onChange={(value) => onChange("householdType", value)}
            />
          </>
        )}
        {step === 5 && (
          <>
            <ChoiceGroup
              id="insuranceType"
              legend={q("insurance_type").legend}
              hint={q("insurance_type").hint}
              choices={optionsToChoices(q("insurance_type"))}
              value={answers.insuranceType}
              error={errors.insuranceType}
              explainerKey={q("insurance_type").explainer_keys?.[0]}
              onChange={(value) => onChange("insuranceType", value)}
            />
            <MultiChoiceGroup
              id="assetIncomeFlags"
              legend={q("asset_income_flags").legend}
              hint={q("asset_income_flags").hint}
              choices={optionsToMultiChoices(q("asset_income_flags"))}
              values={answers.assetIncomeFlags}
              error={errors.assetIncomeFlags}
              explainerKey={q("asset_income_flags").explainer_keys?.[0]}
              onChange={(values, message) => setMulti("assetIncomeFlags", values, message)}
            />
            <ChoiceGroup
              id="vehicleType"
              legend={q("vehicle_type").legend}
              hint={q("vehicle_type").hint}
              choices={optionsToChoices(q("vehicle_type"))}
              value={answers.vehicleType}
              error={errors.vehicleType}
              explainerKey={q("vehicle_type").explainer_keys?.[0]}
              onChange={(value) => onChange("vehicleType", value)}
            />
          </>
        )}
        <div className="action-row action-row--split">
          <Button type="button" variant="secondary" onClick={onBack} disabled={busy}>Back</Button>
          <Button type="submit" busy={busy} disabled={!online}>
            {busy ? "Saving…" : step === 5 ? "Generate my overview" : "Next"}
          </Button>
        </div>
        {!online && <p className="field-hint">You’re offline. Reconnect to save and continue.</p>}
      </form>
    </div>
  );
}
